/**
 * The single definition of "usable product-variant image URL" in warbler.
 *
 * WHY THIS FILE EXISTS
 *
 * The data warehouse writes `'-'` when a variant has no image, and `'-'` is
 * TRUTHY. `PurchaseOrderDisplay.vue:158` guarded only on truthiness, so it
 * rendered `<v-img src="-">` — a broken image box on the merchant's PO. atlas
 * had already hard-coded a `!== '-'` exception, which is the evidence that `'-'`
 * genuinely reaches production data (AS-10982).
 *
 * MUST STAY BEHAVIOURALLY IDENTICAL to the other two copies of this rule:
 *   - assisty-bot  src/helpers/variant_image.js      (AS-10983)
 *   - atlas        frontend/src/lib/variantImage.ts  (AS-10986)
 *
 * That is not stylistic. If they disagree, the backend repairs a value it
 * considers fine while this table renders nothing for it — a defect with no
 * error anywhere. In particular a `startsWith('http')` shortcut is NOT
 * equivalent: it accepts `httpfoo://` and rejects `HTTPS://` and padded URLs.
 *
 * Story: AS-10987 (parent AS-10982)
 */

/** The data-warehouse "no value" sentinel. Truthy — that is the whole problem. */
export const DW_EMPTY = '-';

/**
 * True when `value` is worth rendering into an image element.
 *
 * Non-strings are rejected rather than coerced, so a stray number cannot
 * stringify into something that satisfies the pattern.
 */
export function isUsableImageUrl(value?: string | null): boolean {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed === DW_EMPTY) return false;
  return /^https?:\/\//i.test(trimmed);
}

/**
 * The first of `values` that `isUsableImageUrl` accepts, or `null`.
 *
 * WHY THIS EXISTS (AS-10994)
 *
 * Several templates bind `:src="item.variant_image || item.image"`. `||` yields
 * the first TRUTHY value and `DW_EMPTY` is truthy, so a `variant_image` of `'-'`
 * does not merely render a broken box — it SHADOWS a perfectly good `item.image`
 * sitting right beside it. Those sites were discarding an image they already had.
 *
 * Composes `isUsableImageUrl` rather than restating the rule. The cross-repo
 * contract above covers the RULE, which assisty-bot and atlas also implement;
 * this picker is a warbler-only shape and adds no second definition of "usable".
 *
 * Selects without transforming: the chosen value is returned verbatim, untrimmed.
 * The already-guarded sites bind the raw field, and having one path emit a
 * normalised URL and another the raw one is a second behaviour to keep in sync
 * for no benefit.
 */
export function firstUsableImageUrl(...values: (string | null | undefined)[]): string | null {
  for (const value of values) {
    if (isUsableImageUrl(value)) return value as string;
  }
  return null;
}
